import React, { useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { NonProfitProviderFormConfirmModal } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/NonProfitProviders/components/NonProfitProviderRow/components/NonProfitProviderForm/components/NonProfitProviderFormConfirmModal/NonProfitProviderFormConfirmModal";
import { Button } from "@components/Button";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModalStore } from "@store/modal/modal";
import { useAddNonProfitProviderMutation } from "@services/nonProfits/mutations";
import type { INonProfitProvider } from "@services/nonProfits/types";
import { type IOverviewJointBillService } from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import {
  formatDecimalNumberInputValue,
  getCurrencySymbol,
  parseDecimalNumberInputValue,
} from "@utils/units";
import { COUNTRY_CODE } from "@/constants";
import { CountryCode } from "@/i18n";

interface IBillTotalAmountFormProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  provider: INonProfitProvider;
  periodId: number;
}

interface IFormData {
  amount: string;
}

export const NonProfitProviderForm: React.FC<IBillTotalAmountFormProps> = ({
  jointBillServiceId,
  provider,
  periodId,
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const { create } = useModalStore();

  const { mutateAsync: addNonProfitServiceProvider, isPending } =
    useAddNonProfitProviderMutation({
      jointBillServiceId,
      serviceProviderId: provider.serviceProviderId,
      periodId,
    });

  const { handleSubmit, control, setValue, setError } = useForm<IFormData>({
    defaultValues: {
      amount: formatDecimalNumberInputValue(0, undefined),
    },
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const parseAmountValue = (value: string) =>
    Math.abs(parseDecimalNumberInputValue(value));

  const reformatField = (value: string) => {
    setValue("amount", formatDecimalNumberInputValue(parseAmountValue(value)));
  };

  const submitNonProfitServiceProvider = async (data: IFormData) => {
    const result = await addNonProfitServiceProvider({
      amount: parseAmountValue(data.amount),
    }).catch(setApiErrors);

    if (!isNullOrUndefined(result)) {
      toast.success(t("nonProfitProviders.toast.success"));
    }
  };

  const openConfirmModal = (data: IFormData) =>
    create((modalProps) => (
      <NonProfitProviderFormConfirmModal
        {...modalProps}
        jointBillServiceId={jointBillServiceId}
        periodId={periodId}
        provider={provider}
        amount={parseAmountValue(data.amount)}
      />
    ));

  const submitForm =
    COUNTRY_CODE === CountryCode.LT
      ? submitNonProfitServiceProvider
      : openConfirmModal;

  const [amount] = useWatch({
    name: ["amount"],
    control,
  });

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(submitForm)}
      className="flex w-full flex-col items-end gap-4 md:w-auto md:flex-row"
    >
      <div className="w-full md:w-[120px]">
        <FormInput
          label={t("nonProfitProviders.forms.amountForm.fields.amount.label", {
            currency: getCurrencySymbol(),
          })}
          name="amount"
          control={control}
          inputSize="small"
          onBlur={(e) => reformatField(e.target.value)}
          onFocus={() => {
            if (parseAmountValue(amount) === 0) {
              setValue("amount", "");
            }
          }}
          inputMode="decimal"
        />
      </div>
      <Button
        className="w-full flex-shrink-0 md:w-auto"
        variant="secondary"
        disabled={parseAmountValue(amount) <= 0}
        isLoading={isPending}
      >
        {t("nonProfitProviders.forms.amountForm.actions.submit")}
      </Button>
    </form>
  );
};
